import type { paths } from '@cou/contracts';

/*
 * The admin auth client (API-15, flows/admin-auth). Two credential POSTs — login and the forced
 * password reset — mapped to a discriminated result so the caller keys off the failure `code`
 * (invalid_credentials vs account_inactive vs account_locked are the operator's distinct refusals)
 * rather than the HTTP status. The token this returns is persisted by the admin session store and
 * decoded for nav permissions; this module only mints and re-mints it.
 */

type LoginBody =
  paths['/api/admin/auth/login']['post']['responses'][200]['content']['application/json'];
type ResetBody =
  paths['/api/admin/auth/reset-password']['post']['responses'][200]['content']['application/json'];

export type LoginResult =
  ({ ok: true } & LoginBody) | { ok: false; status: number; code: string | undefined };

export type ResetPasswordResult =
  ({ ok: true } & ResetBody) | { ok: false; status: number; code: string | undefined };

function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

async function failure(response: Response): Promise<{ status: number; code: string | undefined }> {
  const body = (await response.json().catch(() => null)) as { error?: { code?: string } } | null;
  return { status: response.status, code: body?.error?.code };
}

/** Authenticate an operator, returning the admin token and whether a forced reset is pending. */
export async function login(email: string, password: string): Promise<LoginResult> {
  const response = await fetch(`${apiBase()}/api/admin/auth/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (response.ok) {
    return { ok: true, ...((await response.json()) as LoginBody) };
  }
  return { ok: false, ...(await failure(response)) };
}

/** Complete the forced reset with the current (must-reset) token, returning the re-issued token. */
export async function resetPassword(
  adminToken: string,
  newPassword: string,
): Promise<ResetPasswordResult> {
  const response = await fetch(`${apiBase()}/api/admin/auth/reset-password`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${adminToken}`,
    },
    body: JSON.stringify({ newPassword }),
  });

  if (response.ok) {
    return { ok: true, ...((await response.json()) as ResetBody) };
  }
  return { ok: false, ...(await failure(response)) };
}
